import { Select as SelectPrimitive } from '@base-ui/react/select';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A select, wrapping `@base-ui/react/select` (research D12).
 *
 * The inspector needs three of these — step type, gate mode, phase — and a
 * native `<select>` cannot be styled to match the rest of the surface without
 * per-browser hacks. Base UI is already the library every other primitive here
 * wraps, so this adds a component rather than a dependency.
 *
 * Styling goes through semantic brand tokens only; `check:tokens` fails the
 * build on a raw colour.
 */

function Select({ ...props }: SelectPrimitive.Root.Props<string>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectTrigger({
  className,
  children,
  ...props
}: SelectPrimitive.Trigger.Props) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        'border-input bg-background text-foreground flex h-9 w-full items-center pointer-coarse:h-11 justify-between gap-2 rounded-md border px-3 py-1 text-sm',
        'focus-visible:ring-ring transition-colors focus-visible:ring-2 focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon>
        <ChevronDownIcon
          aria-hidden="true"
          className="text-muted-foreground size-4 shrink-0"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectValue({ ...props }: SelectPrimitive.Value.Props) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

/**
 * The options, bounded and scrolling, as the combobox's are.
 *
 * The popup had no height of its own and grew with its options: Copy to
 * workspace's target list opened 673px tall in a 900px window, over the dialog
 * and its footer. It now opens below the trigger and stops at 16rem (the
 * combobox list's bound) or the room the window has, whichever is less, and
 * scrolls inside.
 */
function SelectContent({
  className,
  children,
  ...props
}: SelectPrimitive.Popup.Props) {
  return (
    <SelectPrimitive.Portal>
      {/* Below the trigger, as every other popup in the app opens. Aligning the
          chosen option over the trigger (Base UI's default) sizes the popup to
          the whole window and overrides its max-height inline. */}
      <SelectPrimitive.Positioner
        className="isolate z-50 outline-none"
        sideOffset={4}
        alignItemWithTrigger={false}
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cn(
            'bg-popover text-popover-foreground border-border min-w-[var(--anchor-width)] max-h-[min(var(--available-height),16rem)] overflow-y-auto rounded-md border p-1 shadow-raised',
            'origin-[var(--transform-origin)] transition-[transform,opacity] data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-7 text-sm select-none',
        'data-disabled:text-muted-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemIndicator className="absolute left-1.5 flex size-4 items-center justify-center">
        <CheckIcon aria-hidden="true" className="size-3.5" />
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

/** A rule between groups of options, e.g. an action above the list it adds to. */
function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
